// The one table for every record type (R-U2): virtual rows, sticky header and title column, in-cell editing, keyboard navigation.

import { memo, useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { canWrite, recordClosedFor, type FieldDef, type Id, type RecordObj, type ViewColumn } from '@confdeck/core-shared';
import { FieldValue } from '../fields/FieldValue.tsx';
import { InlineEditor, PopoverEditor, POPOVER_FIELDS, WIDE_FIELDS } from '../fields/FieldEditor.tsx';
import { setField } from '../sync/mutations.ts';
import { useAccess, useMeta, useRecord, useSync } from '../sync/react.tsx';
import { useSetting } from '../settings/use-setting.ts';
import { FIELD_ICONS, Icon } from '../ui/icons.tsx';
import { Checkbox, Chevron, IconButton, Popover, PopoverAnchor, PopoverContent, cx, useNarrow } from '../ui/primitives.tsx';
import { Tooltip } from '../ui/Tooltip.tsx';
import { ColumnMenu } from './ColumnMenu.tsx';
import { pageState, togglePage } from './selection.ts';
import { SYSTEM_COLUMNS, pipelineColumn, systemValue } from './system-columns.tsx';
import s from './table.module.css';

/** `derived` — a column shown from another field (the pipeline of a stage): not sorted or filtered by itself. */
export interface TableColumn { key: string; width: number; field?: FieldDef; label: string; icon: string; derived?: boolean }

interface Props {
  typeId: Id;
  ids: Id[];
  columns: ViewColumn[];
  onColumns: (cols: ViewColumn[]) => void;
  onSort: (field: string, dir: 'asc' | 'desc' | null) => void;
  sortState: { field: string; dir: 'asc' | 'desc' }[];
  onOpen: (id: Id, e?: React.MouseEvent | React.KeyboardEvent) => void;
  /** Without it the table has no «+» column to add a field (a page's own list: its fields are set by the section). */
  onAddField?: () => void;
  /** Without it the column menu has no «Фильтр по полю» (a page without a filter line). */
  onFilterBy?: (field: string) => void;
  /** The record open beside the list, when the screen still shows one; records open as pages now. */
  openId?: Id | null;
  selected: Set<Id>;
  onSelect: (next: Set<Id>) => void;
  lite?: boolean;
  /**
   * Rows in groups under heads of their own (the buckets of the date of «Мои», R-U30): the rows are the groups' ids in this order,
   * an empty group has no head. Pages are off while grouped: a group is read whole.
   */
  groups?: TableGroup[];
}

export interface TableGroup { key: string; label: string; color?: string; ids: Id[] }
type Slot = { head: TableGroup } | { row: number };

const OVERSCAN = 12;
/** One card line matches --ctl-h-lg; the gap matches --space-2. Virtual rows stay absolute. */
const CARD_LINE = 36;
const CARD_GAP = 8;

export function DataTable(p: Props) {
  const meta = useMeta();
  // Pagination is a personal setting (R-U15): scrolling by default, pages of 25…200 rows when the person chose them.
  const paging = useSetting('tables.paging');
  const size = !p.lite && !p.groups && typeof paging === 'string' && paging !== 'scroll' ? Number(paging) : 0;
  const [page, setPage] = useState(0);
  const pages = size ? Math.max(1, Math.ceil(p.ids.length / size)) : 1;
  const at = Math.min(page, pages - 1);
  const ids = useMemo(() => (p.groups ? p.groups.flatMap((g) => g.ids) : size ? p.ids.slice(at * size, at * size + size) : p.ids), [p.ids, p.groups, size, at]);
  // A head of a group takes a slot of a row: rows and heads are placed by their slot.
  const slots = useMemo<Slot[] | null>(() => {
    if (!p.groups) return null;
    const out: Slot[] = [];
    let row = 0;
    for (const g of p.groups) { if (!g.ids.length) continue; out.push({ head: g }); for (let i = 0; i < g.ids.length; i++) out.push({ row: row++ }); }
    return out;
  }, [p.groups]);
  const slotOf = (row: number) => (slots ? slots.findIndex((x) => 'row' in x && x.row === row) : row);
  const total = slots ? slots.length : ids.length;
  useEffect(() => { setPage(0); scrollRef.current?.scrollTo({ top: 0 }); }, [p.typeId, size]);
  useEffect(() => { scrollRef.current?.scrollTo({ top: 0 }); }, [at]);
  const scrollRef = useRef<HTMLDivElement>(null);
  const [rowH, setRowH] = useState(40);
  const [range, setRange] = useState({ from: 0, to: 60 });
  const [active, setActive] = useState<{ row: number; col: number } | null>(null);
  const [editing, setEditing] = useState<{ row: number; col: number; initial?: string } | null>(null);
  const access = useAccess();
  const tableClient = useSync();
  /** A cell takes an edit when the role writes the section, the field is people's and the record is not closed for them (R-T4). */
  const editable = (row: number, col: number) => {
    const f = cols[col]?.field;
    const rec = tableClient.store.record(ids[row]!);
    return !!f && !f.options.managed && !!rec && canWrite(access, p.typeId) && !recordClosedFor(meta, rec, meta.user(tableClient.header?.userId));
  };

  const cols: TableColumn[] = useMemo(() => {
    const out: TableColumn[] = [];
    for (const c of p.columns) {
      if (c.hidden) continue;
      const f = meta.field(p.typeId, c.field);
      // A column of the app's own data (`internal`) is not drawn even if an older view lists it.
      if (f && !f.archivedAt && !f.options.internal) out.push({ key: c.field, width: c.width, field: f, label: f.name, icon: FIELD_ICONS[f.fieldType] ?? 'text' });
      else if (SYSTEM_COLUMNS[c.field]) out.push({ key: c.field, width: c.width, label: SYSTEM_COLUMNS[c.field]!.label, icon: SYSTEM_COLUMNS[c.field]!.icon });
      else { const pc = pipelineColumn(meta, p.typeId, c.field); if (pc) out.push({ key: c.field, width: c.width, label: pc.label, icon: pc.icon, derived: true }); }
    }
    return out;
  }, [p.columns, p.typeId, meta, meta.all.size]);

  // Row height follows the density token, so the density variant changes it without code.
  useLayoutEffect(() => {
    const read = () => setRowH(parseFloat(getComputedStyle(document.documentElement).getPropertyValue('--row-h')) || 40);
    read();
    const mo = new MutationObserver(read);
    mo.observe(document.documentElement, { attributes: true, attributeFilter: ['data-density'] });
    return () => mo.disconnect();
  }, []);

  const cards = useNarrow();
  // Border sits inside the card height, so the lines keep their full token height.
  const cardH = Math.max(cols.length, 1) * CARD_LINE + 2;
  const pitch = cards ? cardH + CARD_GAP : rowH;
  const onScroll = useCallback(() => {
    const el = scrollRef.current;
    if (!el) return;
    const from = Math.max(0, Math.floor(el.scrollTop / pitch) - OVERSCAN);
    const to = Math.min(total, Math.ceil((el.scrollTop + el.clientHeight) / pitch) + OVERSCAN);
    setRange((r) => (r.from === from && r.to === to ? r : { from, to }));
  }, [pitch, total]);
  useEffect(onScroll, [onScroll]);
  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    const ro = new ResizeObserver(onScroll);
    ro.observe(el);
    return () => ro.disconnect();
  }, [onScroll]);

  // Keep the active cell visible while moving with the keyboard.
  useEffect(() => {
    if (!active || !scrollRef.current) return;
    const el = scrollRef.current;
    const top = slotOf(active.row) * pitch, bottom = top + pitch, head = cards ? 0 : rowH;
    if (top < el.scrollTop) el.scrollTop = top;
    else if (bottom + head > el.scrollTop + el.clientHeight) el.scrollTop = bottom + head - el.clientHeight;
  }, [active, pitch, cards, rowH]);

  const move = (dr: number, dc: number) => setActive((a) => {
    const cur = a ?? { row: 0, col: 0 };
    return { row: Math.max(0, Math.min(ids.length - 1, cur.row + dr)), col: Math.max(0, Math.min(cols.length - 1, cur.col + dc)) };
  });

  const onKeyDown = (e: React.KeyboardEvent) => {
    if (editing) return;
    const k = e.key;
    if (k === 'ArrowDown') { e.preventDefault(); move(1, 0); }
    else if (k === 'ArrowUp') { e.preventDefault(); move(-1, 0); }
    else if (k === 'ArrowRight' || (k === 'Tab' && !e.shiftKey)) { e.preventDefault(); move(0, 1); }
    else if (k === 'ArrowLeft' || (k === 'Tab' && e.shiftKey)) { e.preventDefault(); move(0, -1); }
    else if (active && (k === 'Enter' || k === 'F2')) {
      e.preventDefault();
      if (active.col === 0 && k === 'Enter' && !e.shiftKey) p.onOpen(ids[active.row]!, e);
      else if (editable(active.row, active.col)) setEditing({ ...active });
    } else if (active && k === ' ' ) {
      e.preventDefault();
      p.onOpen(ids[active.row]!, e);
    } else if (active && k === 'Escape') setActive(null);
    else if (active && k.length === 1 && !e.metaKey && !e.ctrlKey && !e.altKey) {
      const c = cols[active.col];
      if (c?.field && editable(active.row, active.col) && !POPOVER_FIELDS.has(c.field.fieldType) && c.field.fieldType !== 'checkbox') setEditing({ ...active, initial: k });
    }
  };

  // Row callbacks go through one stable ref: rows stay memoised and only the changed row re-renders.
  const handlers = useRef<RowHandlers>(null!);
  handlers.current = {
    onSelect: (id, row, on, shift) => {
      const next = new Set(p.selected);
      if (shift && active) { const [a, b] = [Math.min(active.row, row), Math.max(active.row, row)]; for (let r = a; r <= b; r++) next.add(ids[r]!); }
      else if (on) next.add(id); else next.delete(id);
      p.onSelect(next);
    },
    onCellClick: (row, col) => { setActive({ row, col }); if (editing && (editing.row !== row || editing.col !== col)) setEditing(null); },
    onCellDouble: (row, col) => { setActive({ row, col }); if (editable(row, col)) setEditing({ row, col }); },
    onOpen: (id, e) => p.onOpen(id, e),
    onEditDone: (mv) => {
      setEditing(null);
      if (mv === 'down') move(1, 0); else if (mv === 'right') move(0, 1); else if (mv === 'left') move(0, -1);
      scrollRef.current?.focus({ preventScroll: true });
    },
  };

  const totalW = 40 + cols.reduce((a, c) => a + c.width, 0) + (p.onAddField && !p.lite ? 44 : 0);
  const resize = (key: string, width: number) => p.onColumns(p.columns.map((c) => (c.field === key ? { ...c, width: Math.max(80, Math.round(width)) } : c)));

  // The status counts the selection (DESIGN.md «Реестр»): rows on screen of the rows the filter found, not of the whole section.
  const fmt = (n: number) => n.toLocaleString('ru-RU');
  const shownText = pages > 1 ? `${fmt(at * size + 1)}–${fmt(at * size + ids.length)}` : fmt(ids.length);

  return (
    <div className={cx(s.wrap, cards && s.cards)}>
      <div className={s.scroll} ref={scrollRef} onScroll={onScroll} tabIndex={0} onKeyDown={onKeyDown} role="grid" aria-rowcount={ids.length}>
        <div style={cards
          ? { width: '100%', height: total * pitch + CARD_GAP, position: 'relative' }
          : { width: totalW, minWidth: '100%', height: (total + 1) * rowH + (p.selected.size || pages > 1 ? 72 : 8), position: 'relative' }}>
          {!cards && <div className={s.head} style={{ width: totalW, minWidth: '100%', height: rowH }} role="row">
            <div className={cx(s.cell, s.check, s.stickyL)} style={{ width: 40 }}>
              {!p.lite && <Checkbox className={s.rowCheck} label="Выбрать все" checked={pageState(p.selected, ids)} onChange={() => p.onSelect(togglePage(p.selected, ids))} />}
            </div>
            {cols.map((c, i) => (
              <HeaderCell key={c.key} col={c} first={i === 0} sort={p.sortState.find((x) => x.field === c.key)?.dir}
                onResize={(w) => resize(c.key, w)} menu={!p.lite && (
                  <ColumnMenu typeId={p.typeId} col={c} onSort={(d) => p.onSort(c.key, d)} onFilter={p.onFilterBy ? () => p.onFilterBy!(c.key) : undefined}
                    onHide={() => p.onColumns(p.columns.map((x) => (x.field === c.key ? { ...x, hidden: true } : x)))}
                    onMove={(dir) => {
                      const list = [...p.columns];
                      const at = list.findIndex((x) => x.field === c.key);
                      const to = at + dir;
                      if (to < 0 || to >= list.length) return;
                      [list[at], list[to]] = [list[to]!, list[at]!];
                      p.onColumns(list);
                    }} />
                )} />
            ))}
            {!p.lite && p.onAddField && <Tooltip label="Добавить поле" side="bottom"><button type="button" className={cx(s.cell, s.addCol)} style={{ width: 44 }} onClick={p.onAddField} aria-label="Добавить поле"><Icon name="plus" size={15} /></button></Tooltip>}
          </div>}
          {slots && slots.slice(range.from, range.to).map((x, i) => {
            const slot = range.from + i;
            const top = cards ? slot * pitch : (slot + 1) * rowH;
            if ('head' in x) return <div key={`g:${x.head.key}`} className={s.groupHead} style={{ top, height: cards ? cardH : rowH, width: cards ? '100%' : totalW }} role="row" aria-label={`${x.head.label}: ${x.head.ids.length}`}>
              {x.head.color && <span className={s.groupDot} style={{ background: `var(--c-${x.head.color}-dot)` }} />}<span>{x.head.label}</span><span className={s.groupCount}>{x.head.ids.length}</span>
            </div>;
            const id = ids[x.row]!;
            return (
              <Row key={id} id={id} row={x.row} num={x.row + 1} cards={cards} top={top} height={cards ? cardH : rowH} cols={cols} width={totalW}
                activeCol={active?.row === x.row ? active.col : -1} editingCol={editing?.row === x.row ? editing.col : -1} editInitial={editing?.row === x.row ? editing.initial : undefined}
                open={p.openId === id} selected={p.selected.has(id)} h={handlers} />
            );
          })}
          {!slots && ids.slice(range.from, range.to).map((id, i) => {
            const row = range.from + i;
            return (
              <Row key={id} id={id} row={row} num={at * size + row + 1} cards={cards} top={cards ? row * pitch : (row + 1) * rowH} height={cards ? cardH : rowH} cols={cols} width={totalW}
                activeCol={active?.row === row ? active.col : -1} editingCol={editing?.row === row ? editing.col : -1} editInitial={editing?.row === row ? editing.initial : undefined}
                open={p.openId === id} selected={p.selected.has(id)} h={handlers} />
            );
          })}
        </div>
      </div>
      {/* No status line under the table (owner 29.10, п.6): the count is announced to screen readers only (DESIGN.md «Реестр»). */}
      {!p.lite && <span className="sr-only" role="status">Показано {shownText} из {fmt(p.ids.length)}</span>}
      {pages > 1 && (
        <nav className={s.pager} aria-label="Страницы">
          {/* To the first and the last page at once (G1 measurement: 2000 pages one by one take minutes). */}
          <IconButton size="sm" icon="chevronsLeft" label="Первая страница" disabled={at === 0} onClick={() => setPage(0)} />
          <IconButton size="sm" icon="chevronLeft" label="Предыдущая страница" disabled={at === 0} onClick={() => setPage(at - 1)} />
          <span className={s.pageNum} aria-current="page">{at + 1} / {pages}</span>
          <IconButton size="sm" icon="chevronRight" label="Следующая страница" disabled={at >= pages - 1} onClick={() => setPage(at + 1)} />
          <IconButton size="sm" icon="chevronsRight" label="Последняя страница" disabled={at >= pages - 1} onClick={() => setPage(pages - 1)} />
        </nav>
      )}
    </div>
  );
}

/** The word after a number (1 запись, 2 записи, 5 записей): one rule of the system, in core/shared. */
export { plural } from '@confdeck/core-shared';

function HeaderCell({ col, first, sort, onResize, menu }: { col: TableColumn; first: boolean; sort?: 'asc' | 'desc'; onResize: (w: number) => void; menu: React.ReactNode }) {
  const startDrag = (e: React.PointerEvent) => {
    e.preventDefault();
    e.stopPropagation();
    const x0 = e.clientX, w0 = col.width;
    const el = e.currentTarget as HTMLElement;
    el.setPointerCapture(e.pointerId);
    const mv = (ev: PointerEvent) => onResize(w0 + ev.clientX - x0);
    const up = () => { el.removeEventListener('pointermove', mv); el.removeEventListener('pointerup', up); };
    el.addEventListener('pointermove', mv);
    el.addEventListener('pointerup', up);
  };
  return (
    <div className={cx(s.cell, s.hcell, first && s.stickyL2)} style={{ width: col.width }} role="columnheader" aria-sort={sort === 'asc' ? 'ascending' : sort === 'desc' ? 'descending' : undefined}>
      <Icon name={col.icon} size={14} className={s.hicon} />
      <span className={s.hlabel}>{col.label}</span>
      {sort && <Chevron dir="down" open={sort === 'asc'} size={13} className={s.sortIcon} />}
      {menu}
      <span className={s.resize} onPointerDown={startDrag} />
    </div>
  );
}

interface RowHandlers {
  onSelect: (id: Id, row: number, on: boolean, shift: boolean) => void;
  onCellClick: (row: number, col: number) => void;
  onCellDouble: (row: number, col: number) => void;
  onOpen: (id: Id, e: React.MouseEvent) => void;
  onEditDone: (move?: 'down' | 'right' | 'left') => void;
}

interface RowProps {
  /** The number shown at the start of the row: through all pages, not within one. */
  num: number;
  id: Id; row: number; top: number; height: number; cols: TableColumn[]; width: number; cards: boolean;
  activeCol: number; editingCol: number; editInitial?: string; open: boolean; selected: boolean;
  h: React.RefObject<RowHandlers>;
}

const Row = memo(function Row(p: RowProps) {
  const rec = useRecord(p.id);
  const meta = useMeta();
  const client = useSync();
  const access = useAccess();
  if (!rec) return null;
  const locked = !canWrite(access, rec.typeId) || !!recordClosedFor(meta, rec, meta.user(client.header?.userId));
  return (
    <div className={cx(s.row, p.open && s.rowOpen, p.selected && s.rowSelected)} style={p.cards ? { top: p.top, height: p.height, left: 'var(--space-2)', right: 'var(--space-2)', width: 'auto' } : { top: p.top, height: p.height, width: p.width, minWidth: '100%' }} role="row" aria-rowindex={p.row + 2} data-id={p.id}>
      <div className={cx(s.cell, s.check, s.stickyL)} style={p.cards ? undefined : { width: 40 }}>
        <span className={s.rowNum}>{p.num}</span>
        <Checkbox className={s.rowCheck} label="Выбрать" checked={p.selected} onChange={(on, e) => p.h.current.onSelect(p.id, p.row, on, (e.nativeEvent as MouseEvent).shiftKey)} onClick={(e) => e.stopPropagation()} />
      </div>
      {p.cols.map((c, i) => (
        <Cell key={c.key} rec={rec} col={c} first={i === 0} cards={p.cards} active={p.activeCol === i} editing={p.editingCol === i} initial={p.editInitial} locked={locked}
          onClick={(e) => { if (i === 0 && !(e.target as HTMLElement).closest('input')) p.h.current.onOpen(p.id, e); p.h.current.onCellClick(p.row, i); }}
          onDouble={() => p.h.current.onCellDouble(p.row, i)} onDone={(mv) => p.h.current.onEditDone(mv)} />
      ))}
    </div>
  );
});

function Cell({ rec, col, first, cards, active, editing, initial, locked, onClick, onDouble, onDone }: {
  rec: RecordObj; col: TableColumn; first: boolean; cards: boolean; active: boolean; editing: boolean; initial?: string; locked: boolean;
  onClick: (e: React.MouseEvent) => void; onDouble: () => void; onDone: (move?: 'down' | 'right' | 'left') => void;
}) {
  const client = useSync();
  const f = col.field;
  const popover = f && POPOVER_FIELDS.has(f.fieldType);
  const content = f ? <FieldValue rec={rec} field={f} /> : systemValue(col.key, rec, client.store.meta);
  const toggle = f?.fieldType === 'checkbox' && !f.options.managed && !locked;
  return (
    <div className={cx(s.cell, s.dcell, first && s.stickyL2, first && s.title, active && s.active, editing && s.editing)} style={cards ? undefined : { width: col.width }} data-label={cards ? col.label : undefined}
      role="gridcell" onClick={(e) => { onClick(e); if (toggle && active) setField(client, rec, f!.key, !rec.data[f!.key]); }}
      onDoubleClick={first ? undefined : onDouble}>
      {editing && f && !popover && !toggle ? <InlineEditor rec={rec} field={f} initial={initial} onDone={onDone} className={s.editor} /> : <span className={s.value}>{content}</span>}
      {/* The way into the record (R-U18): a small ↗, its name under the pointer; the click on the first cell opens it. */}
      {first && <span className={s.openHint} title="Открыть" aria-hidden><Icon name="open" size={14} /></span>}
      {/* The editor's popover exists only while the cell is edited: a closed one per cell cost a provider and an anchor in every row. */}
      {popover && editing && (
        <Popover open={editing} onOpenChange={(o) => { if (!o) onDone(); }}>
          <PopoverAnchor className={s.anchor} />
          <PopoverContent width={col.field && WIDE_FIELDS.has(col.field.fieldType) ? undefined : Math.max(260, col.width)}>
            <PopoverEditor rec={rec} field={f!} onDone={() => onDone()} />
          </PopoverContent>
        </Popover>
      )}
    </div>
  );
}
